import { useMutation } from '@apollo/client/react'
import { Alert, Box, Button, Checkbox, Heading, SimpleGrid, Text, VStack, Field } from '@chakra-ui/react'
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea'
import { GenericFormProps, processSubmit } from '@src/components/sco/Form/formHelpers'
import { FormContainer } from '@src/components/sco/FormContainer'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import topics from '@src/constants/topics'
import { UpsertFgProfilePayload } from '@src/graphql-types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { gql } from '@src/lib/gql'
import React, { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { TextareaAutosizeProps } from 'react-textarea-autosize/dist/declarations/src'

type UserGoalsFormFields = {
  founderNeedsKeys: string[]
  founderNeedsOther: string
}

type UserGoalsFormProps = GenericFormProps<UserGoalsFormFields> & {
  submitControlled?: boolean
  submitTrigger?: number
  hideFounderNeedsOther?: boolean
}

export const UserGoalsForm = (props: UserGoalsFormProps) => {
  const { getBreakpointValue } = useBreakpointContext()
  const { currentUser } = useCurrentUser()
  const [formSuccessMessage, setFormSuccessMessage] = useState<string>(null)
  const [formErrorMessage, setFormErrorMessage] = useState<string>(null)
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false)
  const form = useForm<UserGoalsFormFields>({
    defaultValues: {
      founderNeedsKeys: currentUser?.founderNeedsKeys,
      founderNeedsOther: currentUser?.founderNeedsOther,
    },
  })
  const [upsertUserFounderNeeds] = useMutation(gql`
    mutation ($input: UpsertUserFounderNeedsInput!) {
      upsertUserFounderNeeds(input: $input) {
        user {
          id
          founderNeedsKeys
          founderNeedsOther
        }
      }
    }
  `)

  const handleSubmit = useCallback(
    async (values: UserGoalsFormFields) =>
      processSubmit({
        props,
        values,
        setFormErrorMessage,
        action: async (values: UserGoalsFormFields) => {
          setFormSuccessMessage(null)
          setFormErrorMessage(null)
          return upsertUserFounderNeeds({
            variables: {
              input: {
                founderNeedsKeys: values.founderNeedsKeys || [],
                founderNeedsOther: values.founderNeedsOther,
              },
            },
          })
        },
        afterSuccess: async (values: UserGoalsFormFields, result: UpsertFgProfilePayload) => {
          setFormSuccessMessage('Your goals have been saved!')
        },
      }),
    []
  )

  useEffect(() => {
    if (props.submitTrigger) {
      form.handleSubmit(handleSubmit)()
    }
  }, [props.submitTrigger])

  return (
    <FormContainer {...props.containerProps}>
      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <VStack align={'stretch'} gap={'0.5em'}>
          <Heading>What are your biggest challenges right now?</Heading>
          <Text fontSize={'md'} opacity={0.7}>
            Tell us where you need help so we can curate the right people and resources to help you grow.
            Select all that apply:
          </Text>
        </VStack>

        <VStack align={'stretch'} gap={'1em'} marginTop={'1em'} width={'full'}>
          {formSuccessMessage && (
            <Alert.Root borderRadius={'md'} status={'success'}>
              <Alert.Indicator />
              <Box>{formSuccessMessage}</Box>
            </Alert.Root>
          )}

          {formErrorMessage && (
            <Alert.Root borderRadius={'md'} status={'error'}>
              <Alert.Indicator />
              <Box>{formErrorMessage}</Box>
            </Alert.Root>
          )}

          <Field.Root invalid={!!form.formState.errors.founderNeedsKeys}>
            {/*<Field.Label>*/}
            {/*  What areas do you need help with? <small>Select all that apply.</small>*/}
            {/*</Field.Label>*/}

            <SimpleGrid columns={[1, 1, 2]} gap={4} marginBottom={'1em'}>
              {topics
                .filter(t => t.isPrimary)
                .map((topic, idx) => {
                  return (
                    <Checkbox.Root
                      key={idx}
                      defaultChecked={!!currentUser?.founderNeedsKeys?.find(key => key == topic.key)}
                      value={topic.key}
                      {...form.register('founderNeedsKeys')}
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control>
                        <Checkbox.Indicator />
                      </Checkbox.Control>
                      <Checkbox.Label>{topic.title}</Checkbox.Label>
                    </Checkbox.Root>
                  )
                })}
            </SimpleGrid>
            <Field.ErrorText>{form.formState.errors.founderNeedsKeys?.message}</Field.ErrorText>
          </Field.Root>

          {!props.hideFounderNeedsOther && (
            <Field.Root invalid={!!form.formState.errors.founderNeedsOther}>
              <Field.Label>Anything else you'd like us to know?</Field.Label>
              <AutoResizeTextarea
                {...(props.inputProps as TextareaAutosizeProps)}
                {...form.register('founderNeedsOther', {})}
              />
              <Field.ErrorText>{form.formState.errors.founderNeedsOther?.message}</Field.ErrorText>
            </Field.Root>
          )}

          <Button
            display={props.submitControlled ? 'none' : null}
            loading={form.formState.isSubmitting || isRedirecting}
            marginTop={'1em'}
            size={'lg'}
            type={'submit'}
            width={'full'}
            {...props.submitButtonProps}
          >
            Save
          </Button>
        </VStack>
      </form>
    </FormContainer>
  )
}
